<template>
  <b-modal
    :visible="visible"
    size="md"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    modal-class="lead-details-modal"
    @hide="cancel"
  >
    <div class="lead-details-modal__header">
      <h5>{{ $t('LEAD_FORM.TITLE') }}</h5>
      <p class="lead-details-modal__intro">{{ $t('LEAD_FORM.INTRO') }}</p>
    </div>

    <form @submit.prevent="submit">
      <Input
        raw
        name="leadCustomerName"
        autocomplete="name"
        :label="$t('LEAD_FORM.CUSTOMER_NAME')"
        :value="form.customerName"
        :error="errors.customerName"
        @input="(value) => update('customerName', value)"
      />
      <Input
        raw
        name="leadCorporateName"
        autocomplete="organization"
        :label="$t('LEAD_FORM.CORPORATE_NAME')"
        :value="form.corporateName"
        @input="(value) => update('corporateName', value)"
      />
      <Input
        raw
        type="tel"
        name="leadPhone"
        autocomplete="tel"
        :label="$t('LEAD_FORM.PHONE')"
        :value="form.phone"
        :error="errors.phone"
        @input="(value) => update('phone', value)"
      />
      <Input
        raw
        type="email"
        name="leadEmail"
        autocomplete="email"
        :label="$t('LEAD_FORM.EMAIL')"
        :info="$t('LEAD_FORM.EMAIL_INFO')"
        :value="form.email"
        :error="errors.email"
        @input="(value) => update('email', value)"
      />
      <Input
        raw
        name="leadRegistrationNumber"
        :label="$t('LEAD_FORM.REGISTRATION_NUMBER')"
        :info="$t('LEAD_FORM.OPTIONAL')"
        :value="form.registrationNumber"
        @input="(value) => update('registrationNumber', value)"
      />
      <Input
        raw
        type="textarea"
        rows="3"
        name="leadDeliveryAddress"
        autocomplete="street-address"
        :label="$t('LEAD_FORM.DELIVERY_ADDRESS')"
        :info="$t('LEAD_FORM.DELIVERY_ADDRESS_INFO')"
        :value="form.deliveryAddress"
        @input="(value) => update('deliveryAddress', value)"
      />

      <p v-if="submitError" class="lead-details-modal__submit-error">
        {{ $t('LEAD_FORM.SUBMIT_ERROR') }}
      </p>

      <div class="lead-details-modal__buttons">
        <Button variant="ghost" :disabled="isSubmitting" @click="cancel">
          {{ $t('MODAL.CANCEL') }}
        </Button>
        <Button type="submit" :loading="isSubmitting">
          {{ $t('LEAD_FORM.SUBMIT') }}
        </Button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import Button from '@/components/Button';
import Input from '@/components/Input';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMPTY_FORM = {
  customerName: '',
  corporateName: '',
  phone: '',
  email: '',
  registrationNumber: '',
  deliveryAddress: '',
};

export default {
  name: 'LeadDetailsModal',
  components: {
    Button,
    Input,
  },
  props: {
    visible: Boolean,
    isSubmitting: Boolean,
    submitError: Boolean,
  },
  data() {
    return {
      form: { ...EMPTY_FORM },
      errors: {},
    };
  },
  methods: {
    update(field, value) {
      this.$set(this.form, field, value);
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },
    validate() {
      const errors = {};
      if (!this.form.customerName.trim()) {
        errors.customerName = this.$t('LEAD_FORM.REQUIRED');
      }
      if (!this.form.phone.trim()) {
        errors.phone = this.$t('LEAD_FORM.REQUIRED');
      }
      if (!this.form.email.trim()) {
        errors.email = this.$t('LEAD_FORM.REQUIRED');
      } else if (!EMAIL_PATTERN.test(this.form.email.trim())) {
        errors.email = this.$t('LEAD_FORM.INVALID_EMAIL');
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    submit() {
      if (!this.validate()) {
        return;
      }
      this.$emit(
        'submit',
        Object.fromEntries(
          Object.entries(this.form).map(([key, value]) => [key, value.trim()]),
        ),
      );
    },
    cancel() {
      if (this.isSubmitting) {
        return;
      }
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
.lead-details-modal__header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.lead-details-modal__intro {
  font-size: $font-size-small;
  color: $color-grey-dark;
  margin-top: rem(8px);
}

.lead-details-modal .input {
  margin-bottom: 1rem;
}

.lead-details-modal__submit-error {
  color: $color-error;
  font-size: $font-size-small;
  margin-bottom: 1rem;
}

.lead-details-modal__buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}
</style>
